import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import type { FilterOption } from "./types";
import { getSelectedPreview, getSelectedSummary, toggleSelection } from "./utils";

interface MultiSelectDropdownProps<T extends string | number> {
  label: string;
  allLabel: string;
  selected: T[];
  options: Array<FilterOption<T>>;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onChange: (selected: T[]) => void;
}

const SEARCH_THRESHOLD = 9;

export const MultiSelectDropdown = <T extends string | number>({
  label,
  allLabel,
  selected,
  options,
  open,
  setOpen,
  onChange,
}: MultiSelectDropdownProps<T>) => {
  const [searchQuery, setSearchQuery] = useState("");
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      return;
    }
    const handleOutsideClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open, setOpen]);

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const query = searchQuery.trim().toLowerCase();
    return options.filter((option) => option.label.toLowerCase().includes(query));
  }, [options, searchQuery]);

  return (
    <div className="relative" ref={containerRef}>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <button
        type="button"
        className="w-full min-h-[52px] flex items-center justify-between px-3 py-2 border border-gray-200 rounded-md bg-white shadow-sm hover:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
        onClick={() => setOpen((value) => !value)}
      >
        <div className="min-w-0 text-left">
          <p className="text-sm font-medium text-gray-800 truncate">
            {getSelectedSummary(selected, options, allLabel)}
          </p>
          <p className="text-xs text-gray-500 truncate">{getSelectedPreview(selected, options, allLabel)}</p>
        </div>
        <div className="ml-2 flex items-center gap-2">
          {selected.length > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
              {selected.length}
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</span>
            <button
              type="button"
              className="text-xs font-medium text-red-600 hover:text-red-700 disabled:text-gray-400"
              disabled={selected.length === 0}
              onClick={() => onChange([])}
            >
              Clear
            </button>
          </div>
          {options.length >= SEARCH_THRESHOLD && (
            <div className="px-2 py-2 border-b border-gray-100">
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={`Search ${label.toLowerCase()}...`}
                  className="w-full h-8 pl-7 pr-2 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-200"
                />
              </div>
            </div>
          )}
          <div className="max-h-56 overflow-auto p-1">
            {filteredOptions.map((option) => {
              const isSelected = selected.includes(option.value);
              return (
                <button
                  key={String(option.value)}
                  type="button"
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left text-sm hover:bg-gray-50 ${
                    isSelected ? "bg-blue-50 text-blue-800" : "text-gray-700"
                  }`}
                  onClick={() => onChange(toggleSelection(selected, option.value))}
                >
                  <span
                    className={`w-4 h-4 rounded border flex items-center justify-center ${
                      isSelected ? "border-blue-600 bg-blue-600" : "border-gray-300 bg-white"
                    }`}
                  >
                    {isSelected && <Check className="w-3 h-3 text-white" />}
                  </span>
                  <span className="truncate">{option.label}</span>
                </button>
              );
            })}
            {filteredOptions.length === 0 && (
              <div className="px-3 py-4 text-xs text-gray-500 text-center">No options found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
